import { postJson } from "@/domain/common/client/ApiClient"
import { toDetail } from "@/domain/bracket/client/BracketClient"
import {
  divisionLabel,
  genderLabel,
  sportLabel,
  weightClassLabel,
} from "@/domain/fight/model/Fight"
import { bracketSizePayloadFromJSON } from "@bracket/idl/main/bracket/model/BracketPt"
import {
  BracketDrawAllRequestPayload,
  BracketDrawAllResponsePayload,
  BracketDrawRequestPayload,
  BracketGetAllByTournamentRequestPayload,
  BracketGetAllByTournamentResponsePayload,
  BracketGetByIdRequestPayload,
  BracketGetByIdResponsePayload,
  BracketUpdateVisibilityRequestPayload,
  BracketDrawResponsePayload,
  BracketGetDrawTargetRequestPayload,
  BracketGetDrawTargetResponsePayload,
  BracketRemoveRequestPayload,
} from "@bracket/idl/main/bracket/payload/BracketPayloadPt"
import { EmptyResponsePayload } from "@bracket/idl/main/common/payload/EmptyRequestPayloadPt"
import type {
  BracketDrawFailurePayload,
  BracketDrawTargetPayload,
} from "@bracket/idl/main/bracket/payload/BracketPayloadPt"
import type { WeightClassPayload } from "@bracket/idl/main/fight/model/FightPt"
import type { BracketDetail } from "@/domain/bracket/model/Bracket"

/**
 * 편성 대상 한 줄.
 *
 * 편성 요청에는 서버가 내려준 종목 · 부 · 성별 · 체급 payload 를 **그대로 되돌려 보낸다** —
 * 화면이 다시 조립하면 종목과 체급이 어긋난 요청이 만들어질 수 있다.
 *
 * `bracketId` 가 있으면 이미 편성된 체급이고, `isPublic` 이 공개 여부다 —
 * 편성은 곧 공개가 아니라 운영자가 토글을 켜야 공개 조회에 나간다.
 */
interface AdminDrawTarget {
  key: string
  sportLabel: string
  divisionLabel: string
  genderLabel: string
  weightClassName: string
  playerCount: number
  bracketId: string
  isDrawn: boolean
  /** 편성된 대진표가 공개돼 있는가 */
  isPublic: boolean
  bracketStatusLabel: string
  payload: BracketDrawTargetPayload
}

interface AdminDrawParam {
  target: AdminDrawTarget
  tournamentId: string
  size: string
  isRedraw: boolean
}

interface AdminDrawAllParam {
  tournamentId: string
  size: string
  isRedraw: boolean
}

/** 전체 편성에서 빠진 체급 한 줄 */
interface AdminDrawFailure {
  key: string
  weightClassName: string
  categoryLabel: string
  reason: string
}

/**
 * 전체 편성 결과.
 *
 * 체급 하나가 실패해도 나머지는 편성된다 — 그래서 성공 수 · 건너뛴 수 · 실패 목록을 함께 받는다.
 */
interface AdminDrawAllResult {
  drawnCount: number
  skippedCount: number
  failures: AdminDrawFailure[]
}

/** 미편성 → 편성 완료(비공개) → 공개 세 상태를 한 배지로 읽힌다 */
function toStatusLabel(isDrawn: boolean, isPublic: boolean): string {
  if (!isDrawn) return "미편성"

  return isPublic ? "공개" : "비공개"
}

function toDrawFailure(payload: BracketDrawFailurePayload): AdminDrawFailure {
  const weightClassName = weightClassLabel(payload.weightClass)

  return {
    key: `${payload.sport}-${payload.division}-${payload.gender}-${weightClassName}`,
    weightClassName,
    categoryLabel: [
      sportLabel(payload.sport),
      divisionLabel(payload.division),
      genderLabel(payload.gender),
    ].join(" · "),
    reason: payload.reason,
  }
}

function toDrawTarget(payload: BracketDrawTargetPayload): AdminDrawTarget {
  const weightClassName = weightClassLabel(payload.weightClass)
  const isDrawn = Boolean(payload.bracketId)

  return {
    key: `${payload.sport}-${payload.division}-${payload.gender}-${weightClassName}`,
    sportLabel: sportLabel(payload.sport),
    divisionLabel: divisionLabel(payload.division),
    genderLabel: genderLabel(payload.gender),
    weightClassName,
    playerCount: payload.playerCount,
    bracketId: payload.bracketId,
    isDrawn,
    isPublic: payload.isPublic,
    bracketStatusLabel: toStatusLabel(isDrawn, payload.isPublic),
    payload,
  }
}

/**
 * 대진 관리 클라이언트 (관리자).
 *
 * 경로는 fight-bracket-server 의 `BracketAdminRouter` 와 1:1 대응한다.
 * 편성은 되돌리기 어려운 작업이라 이미 편성된 체급은 `isRedraw` 를 켜야 통과하고,
 * **편성하면 그 즉시 공개 대진표가 된다** — 확정 단계가 없다.
 */
const AdminBracketClient = {
  async getDrawTarget(tournamentId: string): Promise<AdminDrawTarget[]> {
    const request = BracketGetDrawTargetRequestPayload.create({ tournamentId })

    const json = await postJson<unknown>(
      "/bo/bracket/get-draw-target",
      BracketGetDrawTargetRequestPayload.toJSON(request),
    )
    const response = BracketGetDrawTargetResponsePayload.fromJSON(json)

    return response.data.map(toDrawTarget)
  },

  async draw(param: AdminDrawParam): Promise<BracketDetail> {
    const target = param.target.payload
    const request = BracketDrawRequestPayload.create({
      tournamentId: param.tournamentId,
      sport: target.sport,
      division: target.division,
      gender: target.gender,
      weightClass: target.weightClass as WeightClassPayload,
      size: bracketSizePayloadFromJSON(param.size),
      isRedraw: param.isRedraw,
    })

    const json = await postJson<unknown>(
      "/bo/bracket/draw",
      BracketDrawRequestPayload.toJSON(request),
    )
    const response = BracketDrawResponsePayload.fromJSON(json)

    if (!response.data) throw new Error("편성 결과를 받지 못했습니다.")

    return toDetail(response.data)
  },

  /**
   * 대회 전체 편성 — 편성 대상 체급을 고른 규모로 한 번에 뽑는다
   *
   * 이미 편성된 체급은 건너뛴다. `isRedraw` 를 켜면 그 체급까지 다시 뽑는다.
   * 체급 하나가 실패해도 나머지는 편성되므로 실패는 예외가 아니라 결과로 돌아온다.
   */
  async drawAll(param: AdminDrawAllParam): Promise<AdminDrawAllResult> {
    const request = BracketDrawAllRequestPayload.create({
      tournamentId: param.tournamentId,
      size: bracketSizePayloadFromJSON(param.size),
      isRedraw: param.isRedraw,
    })

    const json = await postJson<unknown>(
      "/bo/bracket/draw-all",
      BracketDrawAllRequestPayload.toJSON(request),
    )
    const response = BracketDrawAllResponsePayload.fromJSON(json)

    return {
      drawnCount: response.drawnCount,
      skippedCount: response.skippedCount,
      failures: response.failure.map(toDrawFailure),
    }
  },

  /**
   * 대진표 상세 — 관리자 경로라 **비공개까지** 본다
   *
   * 편성은 곧 공개가 아니라 공개 조회로는 비공개 대진표를 열 수 없다.
   */
  async getById(id: string): Promise<BracketDetail> {
    const request = BracketGetByIdRequestPayload.create({ id })

    const json = await postJson<unknown>(
      "/bo/bracket/get-by-id",
      BracketGetByIdRequestPayload.toJSON(request),
    )
    const response = BracketGetByIdResponsePayload.fromJSON(json)

    if (!response.data) throw new Error("대진표를 찾을 수 없습니다.")

    return toDetail(response.data)
  },

  /** 대회 전체 대진표 — 비공개까지 담아 온다 */
  async getAllByTournament(tournamentId: string): Promise<BracketDetail[]> {
    const request = BracketGetAllByTournamentRequestPayload.create({
      tournamentId,
    })

    const json = await postJson<unknown>(
      "/bo/bracket/get-all-by-tournament",
      BracketGetAllByTournamentRequestPayload.toJSON(request),
    )
    const response = BracketGetAllByTournamentResponsePayload.fromJSON(json)

    return response.data.map(toDetail)
  },

  /**
   * 공개 여부 전환 (**대회 단위**)
   *
   * 대진표는 체급별로 조금씩 내보내는 것이 아니라 한 장으로 낸다.
   * 되돌리기 어려운 「확정」이 아니라 언제든 다시 끌 수 있는 토글이다.
   */
  async updateVisibility(
    tournamentId: string,
    isPublic: boolean,
  ): Promise<void> {
    const request = BracketUpdateVisibilityRequestPayload.create({
      tournamentId,
      isPublic,
    })

    const json = await postJson<unknown>(
      "/bo/bracket/update-visibility",
      BracketUpdateVisibilityRequestPayload.toJSON(request),
    )
    EmptyResponsePayload.fromJSON(json)
  },

  async remove(id: string): Promise<void> {
    const request = BracketRemoveRequestPayload.create({ id })

    const json = await postJson<unknown>(
      "/bo/bracket/remove",
      BracketRemoveRequestPayload.toJSON(request),
    )
    EmptyResponsePayload.fromJSON(json)
  },
}

export { AdminBracketClient, toDrawTarget }
export type {
  AdminDrawAllParam,
  AdminDrawAllResult,
  AdminDrawFailure,
  AdminDrawParam,
  AdminDrawTarget,
}
